import { isDefined, tryCatch, UUID } from "@opendaw/lib-std";
import { Workers } from "../Workers";
// Locally imported bundles live in OPFS `wclap/<sha256>/bundle.tar.gz`, addressed by an `opfs:<sha256>` url
export var WclapStorage;
(function (WclapStorage) {
    WclapStorage.Folder = "wclap";
    WclapStorage.Scheme = "opfs:";
    WclapStorage.FileName = "bundle.tar.gz";
    WclapStorage.isLocal = (url) => url.startsWith(WclapStorage.Scheme);
    WclapStorage.idOf = (url) => url.substring(WclapStorage.Scheme.length);
    WclapStorage.urlOf = (id) => `${WclapStorage.Scheme}${id}`;
    WclapStorage.pathOf = (id) => `${WclapStorage.Folder}/${id}/${WclapStorage.FileName}`;
    WclapStorage.urlFor = async (archive) => WclapStorage.urlOf(UUID.toString(await UUID.sha256(archive)));
    WclapStorage.MetaFileName = "meta.json";
    WclapStorage.TombstonesPath = `${WclapStorage.Folder}/tombstones.json`;
    WclapStorage.store = async (archive) => {
        const url = await WclapStorage.urlFor(archive);
        await WclapStorage.save(WclapStorage.idOf(url), archive);
        return url;
    };
    WclapStorage.save = async (id, archive, storedAt = Date.now()) => {
        await Workers.Opfs.write(WclapStorage.pathOf(id), new Uint8Array(archive));
        await Workers.Opfs.write(`${WclapStorage.Folder}/${id}/${WclapStorage.MetaFileName}`, new TextEncoder().encode(JSON.stringify({ storedAt })));
    };
    WclapStorage.storedAt = (id) => Workers.Opfs.read(`${WclapStorage.Folder}/${id}/${WclapStorage.MetaFileName}`)
        .then(bytes => {
        const parsed = tryCatch(() => JSON.parse(new TextDecoder().decode(bytes)));
        return parsed.status === "success" && typeof parsed.value.storedAt === "number" ? parsed.value.storedAt : 0;
    }, () => 0);
    WclapStorage.remove = async (id) => {
        await Workers.Opfs.delete(`${WclapStorage.Folder}/${id}`);
        await WclapStorage.writeTombstones(WclapStorage.mergeTombstones(await WclapStorage.tombstones(), { [id]: Date.now() }));
    };
    // drops the local copy only, for a deletion that happened on another device
    WclapStorage.discard = (id) => Workers.Opfs.delete(`${WclapStorage.Folder}/${id}`);
    WclapStorage.tombstones = () => Workers.Opfs.read(WclapStorage.TombstonesPath)
        .then(bytes => WclapStorage.parseTombstones(new TextDecoder().decode(bytes)), () => ({}));
    WclapStorage.writeTombstones = (value) => Workers.Opfs.write(WclapStorage.TombstonesPath, new TextEncoder().encode(JSON.stringify(value)));
    WclapStorage.mergeTombstones = (a, b) => Object.fromEntries(Array.from(new Set([...Object.keys(a), ...Object.keys(b)]))
        .map(id => [id, Math.max(a[id] ?? 0, b[id] ?? 0)]));
    WclapStorage.parseTombstones = (text) => {
        const parsed = tryCatch(() => JSON.parse(text));
        if (parsed.status === "failure" || typeof parsed.value !== "object" || parsed.value === null || Array.isArray(parsed.value)) {
            return {};
        }
        return Object.fromEntries(Object.entries(parsed.value).filter(([, time]) => typeof time === "number"));
    };
    WclapStorage.exists = (id) => Workers.Opfs.exists(WclapStorage.pathOf(id));
    const remote = { fetch: undefined };
    // a peer in a live room serves bundles this user does not hold, see ChainedWclapProvider
    WclapStorage.installRemote = (fetcher) => { remote.fetch = fetcher; };
    WclapStorage.load = async (url) => {
        const id = WclapStorage.idOf(url);
        if (await WclapStorage.exists(id)) {
            return WclapStorage.loadId(id);
        }
        if (!isDefined(remote.fetch)) {
            throw new Error(`WebCLAP bundle ${id} is not stored`);
        }
        const archive = await remote.fetch(id);
        if (await WclapStorage.urlFor(archive) !== url) {
            throw new Error(`WebCLAP bundle ${id} arrived with a different hash`);
        }
        await WclapStorage.save(id, archive);
        return archive;
    };
    WclapStorage.loadId = (id) => Workers.Opfs.read(WclapStorage.pathOf(id))
        .then(bytes => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
    WclapStorage.list = () => Workers.Opfs.list(WclapStorage.Folder)
        .then(entries => entries.filter(entry => entry.kind === "directory").map(entry => entry.name), () => []);
})(WclapStorage || (WclapStorage = {}));
